import { Contract, Signer, providers, utils } from "ethers";
import ERC20Abi from "./evm/abis/ERC20.json";
import tokenMessengerAbi from "./evm/abis/TokenMessenger.json";
import messageTransmitterAbi from "./evm/abis/MessageTransmitter.json";
import { CCTPSdkConfigsSet, CCTPSdkNetworkConfigs } from "./interface";
import { Connection, PublicKey, Transaction } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { simulateTransaction } from "@coral-xyz/anchor/dist/esm/utils/rpc";
import { IdlCoder } from "@coral-xyz/anchor/dist/esm/coder/borsh/idl";
import * as anchor from "@coral-xyz/anchor";
import { CCTPTokenMessengerMinterIdl } from "./solana/idls/token-messenger-minter";
import { Wallet } from "@coral-xyz/anchor/dist/cjs/provider";
import { CCTPMessageTransmitterIdl } from "./solana/idls/message-transmitter";
import { solidityKeccak256 } from "ethers/lib/utils";

export enum CCTPDomain {
  Ethereum = 0,
  Avalanche = 1,
  Optimism = 2,
  Arbitrum = 3,
  Solana = 5,
  Base = 6,
  Polygon = 7,
}

export function getUSDCContract(
  usdcContractAddress: string,
  signer: Signer | providers.Provider
) {
  return new Contract(usdcContractAddress, ERC20Abi, signer);
}

export function getTokenMessengerContract(
  messengerContractAddress: string,
  signer: Signer | providers.Provider
) {
  return new Contract(messengerContractAddress, tokenMessengerAbi, signer);
}

export function getMessageTransmitterContract(
  messageTransmitterContractAddress: string,
  signer: Signer | providers.Provider
) {
  return new Contract(
    messageTransmitterContractAddress,
    messageTransmitterAbi,
    signer
  );
}

export async function waitFor<T = void>(options: {
  callback: (
    next: () => void,
    resolve: (data: T) => void,
    reject: (e: unknown) => void
  ) => Promise<void>;
}) {
  const { callback } = options;
  let errorRetryCount = 0;
  let getStatusCount = 0;
  const backoffStrategyFactor = 1.5;
  const throttleInterval = 3 * 1000;

  function setTimer(
    resolve: (data: T) => void,
    reject: (err: unknown) => void
  ) {
    setTimeout(async () => {
      try {
        await callback(
          () => {
            errorRetryCount = 0;
            getStatusCount += 1;
            setTimer(resolve, reject);
          },
          resolve,
          reject
        );
      } catch (e) {
        if (errorRetryCount >= 3) {
          reject(e);
        } else {
          getStatusCount += 1;
          errorRetryCount += 1;
          setTimer(resolve, reject);
        }
      }
    }, throttleInterval * getStatusCount * backoffStrategyFactor);
  }

  return new Promise<T>((resolve, reject) => {
    setTimer(resolve, reject);
  });
}

export async function waitForTransaction(options: {
  rpc: string;
  domain: CCTPDomain;
  txHash: string;
}) {
  const { domain, ...restOptions } = options;

  await waitFor({
    callback: async (next, resolve, reject) => {
      const result = await (domain === CCTPDomain.Solana
        ? getSolanaTransactionStatus
        : getEthTransactionStatus)(restOptions);

      if (result === TransactionStatus.Pending) {
        next();
      } else if (result === TransactionStatus.Failed) {
        reject(new Error("transaction failed"));
      } else {
        resolve();
      }
    },
  });
}

export async function waitForAttestation(options: {
  apiHost: string;
  messageHash: string;
}) {
  return waitFor<string>({
    callback: async (next, resolve, reject) => {
      const attestation = await fetchAttestation(options);

      if (attestation) {
        resolve(attestation);
      } else {
        next();
      }
    },
  });
}

export async function fetchAttestation(options: {
  apiHost: string;
  messageHash: string;
}) {
  const { apiHost, messageHash } = options;

  const response = await fetch(`${apiHost}/v1/attestations/${messageHash}`);

  const responseError = new Error(
    `get circle iris api failed. status: ${response.status}`
  );

  if (response.status === 200) {
    const attestationResponse = await response.json();

    if (
      attestationResponse &&
      attestationResponse.status === "complete" &&
      attestationResponse.attestation
    ) {
      return attestationResponse.attestation;
    } else {
      return;
    }
  } else if (response.status === 404) {
    try {
      const attestationResponse = await response.json();

      // if response status is 404, and the error is message hash not found
      // we don't see it as an error, return empty attestation
      if (attestationResponse.error === "Message hash not found") {
        return;
      }
    } catch (e) {
      console.error(e);
    }
  }

  throw responseError;
}

export async function fetchMessage(options: {
  apiHost: string;
  domain: CCTPDomain;
  transactionHash: string;
}) {
  const { apiHost, domain, transactionHash } = options;

  const response = await fetch(
    `${apiHost}/v1/messages/${domain}/${transactionHash}`
  );

  const responseError = new Error(
    `get circle iris api failed. status: ${response.status}`
  );

  if (response.status === 200) {
    const messageResponse = (await response.json()) as {
      messages: {
        attestation: string;
        message: string;
        eventNonce: string;
      }[];
    };

    return messageResponse.messages[0];
  } else if (response.status === 404) {
    return;
  }

  throw responseError;
}

export async function waitForMessage(options: {
  apiHost: string;
  domain: CCTPDomain;
  transactionHash: string;
}) {
  return waitFor<NonNullable<Awaited<ReturnType<typeof fetchMessage>>>>({
    callback: async (next, resolve, reject) => {
      const message = await fetchMessage(options);

      if (message) {
        resolve(message);
      } else {
        next();
      }
    },
  });
}

enum TransactionStatus {
  Pending,
  Failed,
  Successful,
}

async function getEthTransactionStatus(options: {
  rpc: string;
  txHash: string;
}) {
  const receipt = await getEthTransactionReceipt(options);

  return receipt && receipt.status != null
    ? receipt.status === 0
      ? TransactionStatus.Failed
      : TransactionStatus.Successful
    : TransactionStatus.Pending;
}

export async function getSolanaTransactionStatus(options: {
  rpc: string;
  txHash: string;
}) {
  const { rpc, txHash } = options;
  const connection = getSolanaConnection(rpc);

  const result = await connection.getSignatureStatus(txHash, {
    searchTransactionHistory: true,
  });

  return result.value?.err
    ? TransactionStatus.Failed
    : result.value?.confirmationStatus === "confirmed" ||
      result.value?.confirmationStatus === "processed" ||
      result.value?.confirmationStatus === "finalized"
    ? TransactionStatus.Successful
    : TransactionStatus.Pending;
}

export async function getEthTransactionReceipt(options: {
  rpc: string;
  txHash: string;
}) {
  const { rpc, txHash } = options;

  const provider = new providers.StaticJsonRpcProvider(rpc);

  const receipt = await provider.getTransactionReceipt(txHash);

  return receipt;
}
export function parseLog(
  iface: utils.Interface,
  logs: Array<any>,
  methodName: string
) {
  for (const receiptLog of logs) {
    try {
      let parsedLog = iface.parseLog(receiptLog);

      if (parsedLog.name === methodName) {
        return { log: receiptLog, parsedLog };
      }
    } catch (e) {
      // the correct log might be the other logs in the list
      continue;
    }
  }
}

export function getNetworkConfigs(
  configsSet: CCTPSdkConfigsSet,
  domain: number
) {
  const networkConfig = configsSet.networks?.find(
    (item) => item.domain === domain
  );

  if (!networkConfig) {
    throw new Error(`can't find source network configs. domain: ${domain}`);
  }

  return networkConfig;
}

export function getSolanaConnection(rpc: string) {
  return new Connection(rpc, { commitment: "confirmed" });
}

export function hexToBytes(hex: string): Buffer {
  return Buffer.from(hex.replace("0x", ""), "hex");
}

export function bytesToHex(buffer: Buffer): string {
  return "0x" + buffer.toString("hex");
}

export function decodeMessage(messageHex: string) {
  const versionIndex = 0;
  const versionBytesLength = 4;
  const sourceDomainIndex = 4;
  const sourceDomainBytesLength = 4;
  const destinationDomainIndex = 8;
  const destinationDomainBytesLength = 4;
  const nonceIndex = 12;
  const nonceBytesLength = 8;
  const senderIndex = 20;
  const senderBytesLength = 32;
  const recipientIndex = 52;
  const recipientBytesLength = 32;
  const destinationCallerIndex = 84;
  const destinationCallerBytesLength = 32;
  const messageBodyIndex = 116;

  const message = hexToBytes(messageHex);
  const versionBytes = message.subarray(
    versionIndex,
    versionIndex + versionBytesLength
  );
  const versionHex = utils.hexlify(versionBytes);

  const sourceDomainBytes = message.subarray(
    sourceDomainIndex,
    sourceDomainIndex + sourceDomainBytesLength
  );
  const sourceDomainHex = utils.hexlify(sourceDomainBytes);

  const destinationDomainBytes = message.subarray(
    destinationDomainIndex,
    destinationDomainIndex + destinationDomainBytesLength
  );
  const destinationDomainHex = utils.hexlify(destinationDomainBytes);

  const nonceBytes = message.subarray(
    nonceIndex,
    nonceIndex + nonceBytesLength
  );
  const nonceHex = utils.hexlify(nonceBytes);

  const senderBytes = message.subarray(
    senderIndex,
    senderIndex + senderBytesLength
  );
  const senderHex = utils.hexlify(senderBytes);

  const recipientBytes = message.subarray(
    recipientIndex,
    recipientIndex + recipientBytesLength
  );
  const recipientHex = utils.hexlify(recipientBytes);

  const destinationCallerBytes = message.subarray(
    destinationCallerIndex,
    destinationCallerIndex + destinationCallerBytesLength
  );
  const destinationCallerHex = utils.hexlify(destinationCallerBytes);

  const messageBodyBytes = message.subarray(messageBodyIndex);
  const messageBodyHex = utils.hexlify(messageBodyBytes);

  return {
    version: Number(BigInt(versionHex)),
    sourceDomain: Number(BigInt(sourceDomainHex)) as CCTPDomain,
    destinationDomain: Number(BigInt(destinationDomainHex)) as CCTPDomain,
    nonce: BigInt(nonceHex).toString(),
    sender: senderHex,
    recipient: recipientHex,
    destinationCaller: destinationCallerHex,
    messageBody: messageBodyHex,
  };
}

export async function getSolanaTokenAccount(options: {
  connection: Connection;
  publicKey: PublicKey;
  tokenMintAddress: string;
}) {
  const { connection, publicKey, tokenMintAddress } = options;
  const userTokenAccounts = await connection.getParsedTokenAccountsByOwner(
    publicKey,
    {
      programId: new PublicKey(TOKEN_PROGRAM_ID),
    }
  );
  const userTokenAccount = userTokenAccounts.value.find(
    (item) =>
      item.account.data.parsed?.info?.mint?.toString() === tokenMintAddress
  );

  if (!userTokenAccount) {
    throw new Error("Solana USDC user token account does not exist");
  }

  return userTokenAccount.pubkey;
}

interface FindProgramAddressResponse {
  publicKey: anchor.web3.PublicKey;
  bump: number;
}

export function findProgramAddress(
  label: string,
  programId: PublicKey,
  extraSeeds?: (string | number[] | Buffer | PublicKey)[]
): FindProgramAddressResponse {
  const seeds = [Buffer.from(anchor.utils.bytes.utf8.encode(label))];

  if (extraSeeds) {
    for (const extraSeed of extraSeeds) {
      if (typeof extraSeed === "string") {
        seeds.push(Buffer.from(anchor.utils.bytes.utf8.encode(extraSeed)));
      } else if (Array.isArray(extraSeed)) {
        seeds.push(Buffer.from(extraSeed as number[]));
      } else if (Buffer.isBuffer(extraSeed)) {
        seeds.push(extraSeed);
      } else {
        seeds.push(extraSeed.toBuffer());
      }
    }
  }

  const res = PublicKey.findProgramAddressSync(seeds, programId);

  return { publicKey: res[0], bump: res[1] };
}

export function getPrograms(options: {
  wallet?: Wallet;
  connection?: Connection;
  messageTransmitterProgramPubkey: PublicKey;
  tokenMessengerMinterProgramPubkey: PublicKey;
}) {
  const {
    wallet,
    connection,
    messageTransmitterProgramPubkey,
    tokenMessengerMinterProgramPubkey,
  } = options;

  const provider =
    wallet && connection
      ? new anchor.AnchorProvider(connection, wallet, {
          commitment: "confirmed",
        })
      : undefined;

  const messageTransmitterProgram = new anchor.Program(
    CCTPMessageTransmitterIdl,
    messageTransmitterProgramPubkey,
    provider || ({ connection: null } as any)
  );

  const tokenMessengerMinterProgram = new anchor.Program(
    CCTPTokenMessengerMinterIdl,
    tokenMessengerMinterProgramPubkey,
    provider || ({ connection: null } as any)
  );

  return { messageTransmitterProgram, tokenMessengerMinterProgram };
}

export async function simulateTransactionWithoutSigning(options: {
  connection: Connection;
  messageTransmitterProgram: anchor.Program<anchor.Idl>;
  idl: anchor.Idl;
  instructionName: string;
  tx: Transaction;
}) {
  const { connection, messageTransmitterProgram, idl, instructionName, tx } =
    options;

  const recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

  // we just use a random wallet to be the fee payer in case the simulation failed.
  // we don't use it to sign the transaction and we don't want the simulation to verify the signature
  tx.feePayer = new PublicKey("9vuc7y2kZREgKtXM3yJJba6oMH6Xrq1rJjPX353NGPR6");
  tx.recentBlockhash = recentBlockhash;

  const simulationResult = await simulateTransaction(connection, tx);
  const returnPrefix = `Program return: ${messageTransmitterProgram.programId} `;
  const returnLog = simulationResult.value.logs?.find((l) =>
    l.startsWith(returnPrefix)
  );

  if (!returnLog) {
    throw new Error("View expected return log");
  }

  let returnData = Buffer.from(returnLog.slice(returnPrefix.length), "base64");
  let returnType = idl.instructions.find(
    (item) => item.name === instructionName
  )?.returns;

  if (!returnType) {
    throw new Error("View expected return type");
  }

  const coder = IdlCoder.fieldLayout({ type: returnType }, idl.types);

  return coder.decode(returnData);
}

export async function isNonceUsedOnSolana(options: {
  networkConfigs: CCTPSdkNetworkConfigs;
  sourceDomain: CCTPDomain;
  nonce: number;
}): Promise<boolean> {
  const { networkConfigs, sourceDomain, nonce } = options;
  const connection = getSolanaConnection(networkConfigs.rpc);
  const { messageTransmitterProgram } = getPrograms({
    messageTransmitterProgramPubkey: new PublicKey(
      networkConfigs.cctpMessageTransmitterContractAddress
    ),
    tokenMessengerMinterProgramPubkey: new PublicKey(
      networkConfigs.cctpMessengerContractAddress
    ),
  });

  const messageTransmitterAccount = findProgramAddress(
    "message_transmitter",
    messageTransmitterProgram.programId
  );

  const usedNoncesTx = await messageTransmitterProgram.methods
    .getNoncePda({
      nonce: new anchor.BN(nonce),
      sourceDomain: Number(sourceDomain),
    })
    .accounts({
      messageTransmitter: messageTransmitterAccount.publicKey,
    })
    .transaction();

  const usedNonces: PublicKey = await simulateTransactionWithoutSigning({
    connection,
    messageTransmitterProgram,
    idl: CCTPMessageTransmitterIdl,
    instructionName: "getNoncePda",
    tx: usedNoncesTx,
  });

  const isNonceUsedTx = await messageTransmitterProgram.methods
    .isNonceUsed({
      nonce: new anchor.BN(nonce),
    })
    .accounts({
      usedNonces,
    })
    .transaction();

  const isNonceUsed: boolean = await simulateTransactionWithoutSigning({
    connection,
    messageTransmitterProgram,
    idl: CCTPMessageTransmitterIdl,
    instructionName: "isNonceUsed",
    tx: isNonceUsedTx,
  });

  return isNonceUsed;
}

export async function isNonceUsedOnEVM(options: {
  networkConfigs: CCTPSdkNetworkConfigs;
  sourceDomain: CCTPDomain;
  nonce: number;
}): Promise<boolean> {
  const { networkConfigs, sourceDomain, nonce } = options;
  const contract = getMessageTransmitterContract(
    networkConfigs.cctpMessageTransmitterContractAddress,
    new providers.StaticJsonRpcProvider(networkConfigs.rpc)
  );
  const hashedSourceAndNonce = solidityKeccak256(
    ["uint32", "uint64"],
    [sourceDomain, nonce]
  );
  const isNonceUsed = await contract.usedNonces(hashedSourceAndNonce);

  return isNonceUsed.toNumber() === 1;
}
